import { createFileRoute } from "@tanstack/react-router";
import { ArrowRight, History, X } from "lucide-react";
import { useState } from "react";

import { Cabecera } from "@/components/propuesta/cabecera";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";

export const Route = createFileRoute("/_app/auditoria")({
  component: Auditoria,
});

// ===========================================================================
// Quién cambió qué y cuándo.
//
// Es material contable. Cuando el auditor pregunte por qué un abono de
// S/ 14.729 quedó imputado a tal boleta, la respuesta tiene que estar escrita,
// con nombre y fecha — y con lo que decía ANTES, que es lo que separa un
// registro de una lista de acciones.
//
// Hasta ahora la bitácora solo se leía con un `select` contra la base. Eso, en
// la práctica, es no tenerla: nadie de contabilidad va a abrir una terminal
// para responder una pregunta de auditoría.
// ===========================================================================

const POR_PAGINA = 50;

function Auditoria() {
  const [usuario, setUsuario] = useState("");
  const [accion, setAccion] = useState("");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [entidadId, setEntidadId] = useState("");
  const [pagina, setPagina] = useState(0);

  const hayFiltro = Boolean(usuario || accion || desde || hasta || entidadId);
  const limpiar = () => {
    setUsuario("");
    setAccion("");
    setDesde("");
    setHasta("");
    setEntidadId("");
    setPagina(0);
  };

  // Cualquier cambio de filtro vuelve a la primera página: quedarse en la
  // séptima de un filtro que ahora tiene dos resultados enseña una tabla vacía
  // y parece que no hay nada.
  const filtrar = (poner: () => void) => {
    poner();
    setPagina(0);
  };

  const { data, isPending } = api.bitacora.useQuery(
    ["bitacora", usuario, accion, desde, hasta, entidadId, String(pagina)],
    {
      query: {
        usuario: usuario || undefined,
        accion: accion || undefined,
        entidadId: entidadId || undefined,
        desde: desde || undefined,
        hasta: hasta || undefined,
        pagina,
        porPagina: POR_PAGINA,
      },
    },
  );

  const d = data?.status === 200 ? data.body : null;
  const paginas = d ? Math.ceil(d.total / POR_PAGINA) : 0;

  return (
    <>
      <Cabecera
        migas={["Control"]}
        titulo="Auditoría"
        acciones={
          hayFiltro ? (
            <Button size="sm" variant="outline" onClick={limpiar}>
              <X className="mr-2 size-4" />
              Quitar los filtros
            </Button>
          ) : null
        }
      />

      <p className="-mt-2 max-w-3xl text-[13px] text-muted-foreground">
        Cada cambio queda escrito con quién lo hizo, cuándo, y qué decía antes.
        Lo que hace el sistema solo —la pasada del cruce, por ejemplo— también
        deja su asiento, firmado como automático.
      </p>

      <Card>
        <CardHeader className="gap-3 space-y-0">
          <CardTitle className="font-medium text-base">
            {d ? `${d.total} asientos` : "Bitácora"}
          </CardTitle>

          <div className="flex flex-wrap items-center gap-2">
            <select
              value={usuario}
              onChange={(e) => filtrar(() => setUsuario(e.target.value))}
              className="h-8 rounded-md border bg-background px-2 text-[12.5px]"
            >
              <option value="">Cualquiera</option>
              {(d?.usuarios ?? []).map((u: string) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </select>

            <select
              value={accion}
              onChange={(e) => filtrar(() => setAccion(e.target.value))}
              className="h-8 rounded-md border bg-background px-2 text-[12.5px]"
            >
              <option value="">Cualquier acción</option>
              {(d?.acciones ?? []).map(
                (a: { accion: string; queHizo: string }) => (
                  <option key={a.accion} value={a.accion}>
                    {a.queHizo}
                  </option>
                ),
              )}
            </select>

            <label className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
              desde
              <input
                type="date"
                value={desde}
                onChange={(e) => filtrar(() => setDesde(e.target.value))}
                className="h-8 rounded-md border bg-background px-2 text-[12.5px]"
              />
            </label>
            <label className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
              hasta
              <input
                type="date"
                value={hasta}
                onChange={(e) => filtrar(() => setHasta(e.target.value))}
                className="h-8 rounded-md border bg-background px-2 text-[12.5px]"
              />
            </label>

            <input
              value={entidadId}
              onChange={(e) => filtrar(() => setEntidadId(e.target.value))}
              placeholder="id de un movimiento, archivo o voucher"
              className="h-8 w-64 rounded-md border bg-background px-2 text-[12.5px] placeholder:text-muted-foreground/60"
            />
          </div>
        </CardHeader>

        <CardContent className="px-0">
          {isPending ? (
            <div className="space-y-2 px-6">
              {["a", "b", "c"].map((k) => (
                <Skeleton key={k} className="h-12" />
              ))}
            </div>
          ) : !d || d.asientos.length === 0 ? (
            <p className="px-6 py-10 text-center text-muted-foreground text-sm">
              {hayFiltro
                ? "Ningún asiento cuadra con esos filtros."
                : "Todavía no hay nada registrado."}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[12.5px]">
                <thead className="border-b text-[11px] text-muted-foreground uppercase tracking-wide">
                  <tr>
                    <th className="py-2 pl-6 text-left font-medium">Cuándo</th>
                    <th className="py-2 pl-6 text-left font-medium">Quién</th>
                    <th className="py-2 pl-6 text-left font-medium">
                      Qué hizo
                    </th>
                    <th className="py-2 pl-6 text-left font-medium">
                      Sobre qué
                    </th>
                    <th className="px-6 py-2 text-left font-medium">
                      Qué cambió
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {d.asientos.map((a: Asiento) => (
                    <Fila
                      key={a.id}
                      a={a}
                      alSeguir={() =>
                        filtrar(() => setEntidadId(a.entidadId ?? ""))
                      }
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>

        {paginas > 1 ? (
          <div className="flex items-center justify-between border-t px-6 py-3">
            <p className="text-[12px] text-muted-foreground">
              página {pagina + 1} de {paginas}
            </p>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={pagina === 0}
                onClick={() => setPagina((p) => Math.max(0, p - 1))}
              >
                Anterior
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={pagina + 1 >= paginas}
                onClick={() => setPagina((p) => p + 1)}
              >
                Siguiente
              </Button>
            </div>
          </div>
        ) : null}
      </Card>
    </>
  );
}

type Asiento = {
  id: string;
  momento: string;
  usuario: string | null;
  accion: string;
  queHizo: string;
  entidad: string | null;
  entidadId: string | null;
  nota: string | null;
  cambios: Array<{
    campo: string;
    antes: string | null;
    despues: string | null;
  }>;
};

function Fila({ a, alSeguir }: { a: Asiento; alSeguir: () => void }) {
  return (
    <tr className="border-b align-top last:border-0">
      <td className="whitespace-nowrap py-2.5 pl-6 tabular-nums">
        {cuando(a.momento)}
      </td>
      <td className="py-2.5 pl-6">
        {a.usuario ? (
          a.usuario
        ) : (
          // Sin firma es el sistema: una pasada del cruce, un worker. Decirlo
          // es mejor que dejar la celda en blanco, que se lee como un dato que
          // se perdió.
          <span className="text-muted-foreground">automático</span>
        )}
      </td>
      <td className="py-2.5 pl-6 font-medium">{a.queHizo}</td>
      <td className="py-2.5 pl-6">
        <span className="text-muted-foreground">{a.entidad ?? "—"}</span>
        {a.entidadId ? (
          <button
            type="button"
            onClick={alSeguir}
            title="Ver todo lo que le pasó a esto"
            className="ml-1.5 inline-flex items-center gap-1 rounded px-1 font-mono text-[11px] text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            {a.entidadId.slice(-8)}
            <History className="size-3" />
          </button>
        ) : null}
      </td>
      <td className="px-6 py-2.5">
        {a.cambios.length > 0 ? (
          <ul className="space-y-0.5">
            {a.cambios.map((c) => (
              <li key={c.campo} className="flex flex-wrap items-center gap-1.5">
                <span className="text-muted-foreground">{c.campo}</span>
                <span className="tabular-nums">{c.antes ?? "—"}</span>
                <ArrowRight className="size-3 shrink-0 text-muted-foreground" />
                <span className="font-medium tabular-nums">
                  {c.despues ?? "—"}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          // Sin antes ni después, la nota es todo lo que hay. Las operaciones
          // que solo dejan nota son las que aún no guardan el valor anterior.
          <span className="text-muted-foreground">{a.nota ?? "—"}</span>
        )}
        {a.cambios.length > 0 && a.nota ? (
          <p className="mt-1 text-[11px] text-muted-foreground">{a.nota}</p>
        ) : null}
      </td>
    </tr>
  );
}

/** El momento, en hora local y sin segundos: lo que hace falta para auditar. */
function cuando(iso: string): string {
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${dd}-${mm}-${d.getFullYear()} ${hh}:${mi}`;
}
